// One active vocabulary shared by browser and server. Aliases are read-only
// compatibility/provenance, never accepted by active writers.
export const PROJECT_TRACKS = Object.freeze([
  { key: "it_av_network", label: "IT-AV-Network" },
  { key: "workplace", label: "Workplace" },
  { key: "design", label: "Design" },
  { key: "security", label: "Security" },
]);
export const PROJECT_TRACK_KEYS = Object.freeze(PROJECT_TRACKS.map(t => t.key));
export const OWNERSHIP_SLOTS = Object.freeze([
  { key: "market_pm", label: "Market PM (JLL)" },
  { key: "pmo_pm", label: "PMO PM (JLL)" },
  { key: "delivery_owner", label: "Delivery Owner (Ripple)" },
]);
export const LEGACY_TRACK_MAP = Object.freeze({
  it: "it_av_network", av: "it_av_network", network: "it_av_network",
  office_move: "workplace", furniture_layout: "workplace",
  design: "design", phys_sec: "security",
});
export const canonicalTrack = key => LEGACY_TRACK_MAP[key] || key;
export const isDeliveryOwner = a => (a.trackKey ?? a.track_key) == null
  && (a.ownershipSlot ?? a.ownership_slot ?? "delivery_owner") === "delivery_owner";

const at = r => {
  const value = r.createdAt || r.setAt || r.updatedAt;
  return (value instanceof Date ? value.getTime() : Date.parse(value || "")) || 0;
};
// PostgreSQL text keeps sub-millisecond order; stable sort retains the captured
// order if an older snapshot contains only serialized millisecond timestamps.
const chronological = (a, b) => at(a) - at(b) ||
  (a.chronologyKey && b.chronologyKey ? a.chronologyKey.localeCompare(b.chronologyKey) : 0);
const severity = { blocked: 3, red: 3, watch: 2, amber: 2, on_track: 1, green: 1, not_started: 0 };
const sourced = r => ({ ...r, sourceTrackKey: r.sourceTrackKey || r.trackKey, trackKey: canonicalTrack(r.trackKey) });

/** No live lookups, mutation or inferred membership. Every captured line survives.
 * Conflicting RAG uses worst severity; quiet requires all captured source scopes,
 * unless a subsequent canonical response explicitly acknowledges the whole slot.
 */
export function projectTrackPresentation(project) {
  const lines = (project.lines || []).map(sourced).sort(chronological);
  const sourceStatuses = (project.statuses || []).flatMap(s => s.sourceStatuses?.length
    ? s.sourceStatuses.map(source => ({
      ...source,
      ...(source.setByPersonId === s.setByPersonId ? { setterName: s.setterName, setterOrg: s.setterOrg } : {}),
    })) : [s]).map(sourced);
  const statuses = PROJECT_TRACK_KEYS.flatMap(key => {
    const sources = sourceStatuses.filter(s => s.trackKey === key);
    if (!sources.length) return [];
    const winner = [...sources].sort((a, b) => (severity[b.status] || 0) - (severity[a.status] || 0) || chronological(b, a))[0];
    return [{ ...winner, trackKey: key, sourceStatuses: sources,
      // RAG selection must not erase newer report chronology.
      reportAt: new Date(Math.max(...sources.map(at))).toISOString() }];
  });
  const sourceResponses = (project.sourceTrackResponses || project.trackResponses || []).map(sourced).sort(chronological);
  const captured = Array.isArray(project.assignedTrackKeys) ? [...project.assignedTrackKeys] : null;
  const responseScopes = project.responseScopeTrackKeys || captured;
  const trackResponses = PROJECT_TRACK_KEYS.flatMap(key => {
    const sources = sourceResponses.filter(r => r.trackKey === key);
    if (!sources.length) return [];
    const latest = sources.at(-1);
    if (latest.sourceTrackKey === key) return [latest];
    const scopes = responseScopes?.filter(k => canonicalTrack(k) === key);
    const lastByScope = new Map(sources.map(r => [r.sourceTrackKey, r]));
    const allQuiet = scopes?.length && scopes.every(k => {
      const response = lastByScope.get(k);
      const reports = [
        ...sourceStatuses.filter(s => s.sourceTrackKey === k),
        ...lines.filter(l => l.sourceTrackKey === k).flatMap(l => [l, ...(l.corrections || [])]),
      ];
      return response?.response === "quiet" && at(response) >= Math.max(0, ...reports.map(at));
    });
    // Do not present a partial legacy quiet as a whole consolidated response.
    return allQuiet ? [{ ...latest, response: "quiet" }]
      : sources.filter(r => r.response === "reporting").slice(-1);
  });
  return { ...project, lines, statuses, trackResponses, sourceTrackResponses: sourceResponses,
    ...(captured ? {
      assignedTrackKeys: PROJECT_TRACK_KEYS.filter(k => captured.some(s => canonicalTrack(s) === k)),
      responseScopeTrackKeys: responseScopes,
    } : {}) };
}

export function weeklyTrackPresentation(model) {
  if (!model || !Array.isArray(model.projects)) return model;
  return { ...model, trackKeys: PROJECT_TRACK_KEYS, projects: model.projects.map(projectTrackPresentation) };
}

export function legacyTracksPresentation(tracks) {
  return PROJECT_TRACK_KEYS.flatMap(key => {
    const sources = (tracks || []).filter(t => canonicalTrack(t.trackKey) === key);
    if (!sources.length) return [];
    const winner = [...sources].sort((a,b) => (severity[b.status] || 0) - (severity[a.status] || 0) || chronological(b,a))[0];
    return [{ ...winner, trackKey: key, sourceTracks: sources }];
  });
}

export function snapshotTrackPresentation(payload) {
  if (!payload) return payload;
  return { ...payload, weeklyOverview: weeklyTrackPresentation(payload.weeklyOverview),
    ...(Array.isArray(payload.readOuts) ? { readOuts: payload.readOuts.map(p => ({ ...p, tracks: legacyTracksPresentation(p.tracks) })) } : {}) };
}
